import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import { PaginationProperties, TableDataProperties } from '../../../shared/uiComponents/Table/tableProperties';
import { EventDataProperties } from '../../API/ClientAPIHelpers/calendarProperties';
import {
	AdminDashboardProperties,
	DashboardAuthorizationProperties,
	DashboardTemporaryAuthorizationProperties,
} from '../../API/ClientAPIHelpers/userClientProperties';

export interface FlagggedSessionsTableData extends TableDataProperties {
	query: EventDataProperties[] | null;
}

interface DashboardStateProperties {
	authorizationBCBA: DashboardAuthorizationProperties[] | null;
	authorizations: AdminDashboardProperties;
	error: ErrorProperties;

	loading: boolean;
	sessions: FlagggedSessionsTableData;
	temporaryAuthorizations: DashboardTemporaryAuthorizationProperties;
}

const initialDashboardState: DashboardStateProperties = {
	loading: false,
	sessions: { query: null },
	authorizations: { query: null },
	temporaryAuthorizations: { query: null },
	authorizationBCBA: null,
	error: {
		title: '',
		status: 0,
	},
};

export const getTemporaryAuthorizations = createAsyncThunk(
	'/dashboard/admin/temporary',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getTemporaryAuthorizations(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as DashboardTemporaryAuthorizationProperties;
	},
);

export const getFlaggedSessions = createAsyncThunk(
	'/dashboard/admin/flagged',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getFlaggedSessions(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as FlagggedSessionsTableData;
	},
);

export const getAuthorizationInfoBCBA = createAsyncThunk('/dashboard/bcba', async (userId: string, thunkAPI) => {
	const response = await API.ClientAPI.Dashboard.getAuthorizationInfoBCBA(userId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getAuthorizationInfoClinicalSupervisor = createAsyncThunk(
	'/dashboard/cs',
	async ({ userId, data }: { data: PaginationProperties; userId: string }, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getAuthorizationInfoClinicalSupervisor({
			userId,
			data,
		});

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AdminDashboardProperties;
	},
);

export const getAuthorizationInfoAdmin = createAsyncThunk(
	'/dashboard/admin',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getAuthorizationInfoAdmin(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AdminDashboardProperties;
	},
);

const dashboardSlice = createSlice({
	initialState: initialDashboardState,
	name: 'dashboard',
	reducers: {},
	extraReducers: (builder) => {
		// getTemporaryAuthorizations
		builder.addCase(getTemporaryAuthorizations.pending, (state) => {
			state.loading = true;
			state.error = initialDashboardState.error;
		});
		builder.addCase(getTemporaryAuthorizations.fulfilled, (state, action) => {
			state.loading = false;
			state.temporaryAuthorizations = action.payload;
		});
		builder.addCase(getTemporaryAuthorizations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getFlaggedSessions
		builder.addCase(getFlaggedSessions.pending, (state) => {
			state.loading = true;
			state.error = initialDashboardState.error;
		});
		builder.addCase(getFlaggedSessions.fulfilled, (state, action) => {
			state.loading = false;
			state.sessions = action.payload;
		});
		builder.addCase(getFlaggedSessions.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAuthorizationInfoBCBA
		builder.addCase(getAuthorizationInfoBCBA.pending, (state) => {
			state.loading = true;
			state.error = initialDashboardState.error;
		});
		builder.addCase(getAuthorizationInfoBCBA.fulfilled, (state, action) => {
			state.loading = false;
			state.authorizationBCBA = action.payload;
		});
		builder.addCase(getAuthorizationInfoBCBA.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAuthorizationInfoClinicalSupervisor
		builder.addCase(getAuthorizationInfoClinicalSupervisor.pending, (state) => {
			state.loading = true;
			state.error = initialDashboardState.error;
		});
		builder.addCase(getAuthorizationInfoClinicalSupervisor.fulfilled, (state, action) => {
			state.loading = false;
			state.authorizations = action.payload;
		});
		builder.addCase(getAuthorizationInfoClinicalSupervisor.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAuthorizationInfoAdmin
		builder.addCase(getAuthorizationInfoAdmin.pending, (state) => {
			state.loading = true;
			state.error = initialDashboardState.error;
		});
		builder.addCase(getAuthorizationInfoAdmin.fulfilled, (state, action) => {
			state.loading = false;
			state.authorizations = action.payload;
		});
		builder.addCase(getAuthorizationInfoAdmin.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
	},
});

export default dashboardSlice.reducer;
